let sdkPromise;
const sdkURL = 'https://connect.facebook.net/pt_BR/sdk.js';
// sdk.js first installs a queue-only facade. The bundle replaces window.FB.
const isSDKReady = (sdk) =>
  sdk &&
  !sdk.__buffer &&
  typeof sdk.init === 'function' &&
  typeof sdk.login === 'function';

const sdkError = (code, description, startedAt, detail = '') => {
  const error = new Error(
    [
      `Não foi possível carregar a Meta [${code}].`,
      description,
      `Script: ${sdkURL}`,
      `Origem do front: ${window.location.origin}`,
      `Tempo decorrido: ${Date.now() - startedAt} ms. Navegador: ${navigator.onLine ? 'online' : 'offline'}.`,
      detail,
    ]
      .filter(Boolean)
      .join('\n')
  );
  error.code = code;
  return error;
};

export const loadFacebookSDK = (options) => {
  if (
    !options?.app_id ||
    !options?.config_id ||
    !/^v\d+\.\d+$/.test(options?.version || '')
  ) {
    return Promise.reject(
      new Error('Configuração do Embedded Signup incompleta na API.')
    );
  }
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      if (isSDKReady(window.FB)) return resolve(window.FB);
      const startedAt = Date.now();
      let script = document.getElementById('facebook-jssdk');
      let settled = false;
      const previousAsyncInit = window.fbAsyncInit;
      const cleanup = () => {
        clearTimeout(timer);
        script?.removeEventListener('load', loaded);
        script?.removeEventListener('error', failed);
        document.removeEventListener(
          'securitypolicyviolation',
          policyViolation
        );
        if (window.fbAsyncInit === asyncInit) {
          if (previousAsyncInit === undefined) delete window.fbAsyncInit;
          else window.fbAsyncInit = previousAsyncInit;
        }
      };
      const fail = (code, description, detail) => {
        if (settled) return;
        settled = true;
        cleanup();
        // Keep a loading facade's script: reinserting sdk.js while FB exists
        // does nothing. A late bundle can still complete through fbAsyncInit.
        if (!window.FB) script?.remove();
        reject(sdkError(code, description, startedAt, detail));
      };
      const failed = () => {
        fail(
          'SDK_SCRIPT_ERROR',
          'O navegador disparou um erro ao carregar ou executar o script da Meta.',
          'O evento não informa o status HTTP nem distingue bloqueador, DNS, TLS ou CORS. Abra F12 → Rede (Network), filtre por sdk.js e consulte o status/motivo da requisição. Confira também o Console.'
        );
      };
      const policyViolation = (event) => {
        if (event.disposition !== 'enforce') return;
        try {
          if (
            new URL(event.blockedURI).origin !== 'https://connect.facebook.net'
          )
            return;
        } catch {
          return;
        }
        fail(
          'SDK_CSP_BLOCKED',
          'A política de segurança da página (CSP) bloqueou o SDK da Meta.',
          `Diretiva: ${event.effectiveDirective || event.violatedDirective}. Recurso bloqueado: ${event.blockedURI}.`
        );
      };
      const loaded = () => {
        if (!window.FB)
          return fail(
            'SDK_GLOBAL_MISSING',
            'O script disparou o evento load, mas não disponibilizou window.FB.',
            'Confira no Console se houve erro de execução e na aba Rede se a resposta de sdk.js contém o SDK esperado.'
          );
        // A script load event only confirms the bootstrap, not its bundle.
        if (!isSDKReady(window.FB) || settled) return;
        settled = true;
        cleanup();
        resolve(window.FB);
      };
      const asyncInit = () => {
        try {
          if (
            typeof previousAsyncInit === 'function' &&
            previousAsyncInit.hasRun !== true
          ) {
            previousAsyncInit.hasRun = true;
            previousAsyncInit();
          }
        } finally {
          loaded();
        }
      };
      const timer = setTimeout(
        () =>
          fail(
            window.FB ? 'SDK_NOT_READY' : 'SDK_TIMEOUT',
            window.FB
              ? 'O arquivo inicial da Meta carregou, mas o SDK completo não ficou pronto em 20 segundos.'
              : 'O carregamento do SDK não terminou em 20 segundos.',
            'Abra F12 → Rede (Network) e verifique também /pt_BR/bundle/sdk.js/ e os demais recursos de connect.facebook.net. Após corrigir um bloqueio, recarregue a página.'
          ),
        20000
      );
      window.fbAsyncInit = asyncInit;
      document.addEventListener('securitypolicyviolation', policyViolation);
      const isNew = !script;
      if (!script) {
        script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = sdkURL;
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
      }
      script.addEventListener('load', loaded);
      script.addEventListener('error', failed);
      if (isNew) {
        try {
          document.head.appendChild(script);
        } catch (error) {
          fail(
            'SDK_INSERT_ERROR',
            'O navegador impediu a inclusão do script na página.',
            error.message
          );
        }
      }
    }).catch((error) => {
      sdkPromise = undefined;
      throw error;
    });
  }
  return sdkPromise.then((sdk) => {
    try {
      sdk.init({
        appId: options.app_id,
        version: options.version,
        xfbml: false,
        cookie: false,
      });
    } catch (error) {
      throw sdkError(
        'SDK_INIT_ERROR',
        'O SDK foi carregado, mas FB.init falhou.',
        Date.now(),
        `Versão Graph API: ${options.version}. Detalhe: ${error.message || String(error)}`
      );
    }
    return sdk;
  });
};

export const parseSignupMessage = (event) => {
  if (
    !['https://www.facebook.com', 'https://web.facebook.com'].includes(
      event.origin
    )
  )
    return null;
  try {
    const message =
      typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    return message?.type === 'WA_EMBEDDED_SIGNUP' ? message : null;
  } catch {
    return null;
  }
};

// Code and account IDs arrive independently, in either order. Submit once only.
export const startEmbeddedSignup = ({
  sdk,
  options,
  onSubmit,
  onError,
  onStatus = () => {},
}) => {
  let code;
  let account;
  let stopped = false;
  let timer;
  let feedbackTimer;
  const stop = () => {
    stopped = true;
    code = undefined;
    account = undefined;
    clearTimeout(timer);
    clearTimeout(feedbackTimer);
    window.removeEventListener('message', receive);
  };
  const fail = (message) => {
    if (stopped) return;
    stop();
    onError(message);
  };
  const submit = () => {
    if (stopped || !code || !account) return;
    const payload = { ...account, code };
    stop();
    onSubmit(payload);
  };
  const receive = (event) => {
    const message = parseSignupMessage(event);
    if (!message || stopped) return;
    clearTimeout(feedbackTimer);
    if (message.event === 'CANCEL')
      return fail(
        'Configuração cancelada na Meta. Você pode tentar novamente.'
      );
    if (message.event === 'ERROR')
      return fail(
        `A Meta não concluiu a configuração [META_SIGNUP_ERROR]. ${message.data?.error_message || 'Consulte o erro exibido na janela da Meta.'}`
      );
    if (
      ![
        'FINISH',
        'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
        'FINISH_ONLY_WABA',
      ].includes(message.event)
    )
      return;
    const { waba_id, phone_number_id, business_id } = message.data || {};
    const coexistencia =
      message.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';
    const validId = (id) => typeof id === 'string' && /^\d+$/.test(id);
    if (
      !validId(waba_id) ||
      (!coexistencia && !validId(phone_number_id)) ||
      (phone_number_id != null && !validId(phone_number_id))
    ) {
      return fail(
        'A Meta não retornou a conta e o número necessários. Repita o fluxo selecionando um número de telefone.'
      );
    }
    account = { waba_id };
    if (phone_number_id) account.phone_number_id = phone_number_id;
    if (coexistencia) {
      account.coexistencia = true;
      account.signup_event = message.event;
    }
    if (!code)
      onStatus(
        'Número selecionado na Meta. Aguardando o código de autorização.'
      );
    if (typeof business_id === 'string' && /^\d+$/.test(business_id))
      account.business_id = business_id;
    submit();
  };
  window.addEventListener('message', receive);
  timer = setTimeout(
    () =>
      fail(
        'O tempo de configuração expirou. Feche a janela da Meta antes de tentar novamente.'
      ),
    10 * 60 * 1000
  );
  feedbackTimer = setTimeout(() => {
    if (stopped) return;
    onStatus(
      'Se a janela da Meta não abriu, verifique o ícone de popup bloqueado na barra de endereço do navegador e permita popups para este site.'
    );
  }, 12000);
  try {
    if (sdk?.__buffer) {
      fail(
        'O SDK da Meta ainda está carregando [SDK_NOT_READY]. Recarregue a página e verifique os recursos de connect.facebook.net.'
      );
      return stop;
    }
    if (typeof sdk?.login !== 'function') {
      fail(
        'O SDK não disponibilizou FB.login [SDK_LOGIN_UNAVAILABLE]. Recarregue a página e confira se os recursos da Meta foram bloqueados.'
      );
      return stop;
    }
    // Called synchronously from the click; awaiting here can block the popup.
    sdk.login(
      (response) => {
        if (stopped) return;
        clearTimeout(feedbackTimer);
        if (response?.error)
          return fail(
            `A Meta recusou a autorização [META_LOGIN_ERROR]. ${response.error.message || response.error.error_user_msg || 'Consulte o Console do navegador.'}`
          );
        if (!response?.authResponse?.code)
          return fail(
            'A autorização não foi concluída. Verifique se o navegador permitiu abrir a janela da Meta.'
          );
        code = response.authResponse.code;
        if (!account)
          onStatus(
            'Autorização recebida. Aguardando a Meta informar a conta e o número.'
          );
        clearTimeout(timer);
        timer = setTimeout(
          () =>
            fail(
              'A Meta autorizou o acesso, mas não retornou os dados do número. Repita a configuração.'
            ),
          30000
        );
        submit();
      },
      {
        config_id: options.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: options.extras || {},
      }
    );
  } catch (error) {
    fail(
      `Não foi possível abrir a configuração da Meta [SDK_LOGIN_ERROR]. ${error.message || String(error)}`
    );
  }
  return stop;
};
